/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { shipmentsService } from '../services/shipmentsService'
import { geocodingService } from '../services/geocodingService'
import { getShipmentRequesterName } from '../utils/shipmentHelpers'

const LocationModal = ({ shipment, onClose }) => {
  const [locationData, setLocationData] = useState(null)
  const [addressData, setAddressData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (shipment?.shipmentId) {
      fetchLocation()
    }
  }, [shipment])

  const fetchLocation = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const result = await shipmentsService.getCurrentLocation(shipment.shipmentId)
      
      if (result.success) {
        setLocationData(result.data)
        
        // If location data has coordinates, fetch the address
        if (result.data?.currentLocation?.latitude && result.data?.currentLocation?.longitude) {
          const addressResult = await geocodingService.reverseGeocode(
            result.data.currentLocation.latitude,
            result.data.currentLocation.longitude
          )
          
          if (addressResult.success) {
            setAddressData(addressResult.data)
          }
        }
      } else {
        setError(result.message || 'Failed to fetch location data')
        toast.error(result.message || 'Failed to fetch location data')
      }
    } catch (error) {
      setError('Error fetching location data')
      toast.error('Error fetching location data')
    } finally {
      setLoading(false)
    }
  }

  const getGoogleMapsUrl = () => {
    if (!locationData?.currentLocation || !shipment?.shipmentData) return null
    
    const { latitude, longitude } = locationData.currentLocation
    const pickup = encodeURIComponent(shipment.shipmentData.pickupLocation || '')
    const drop = encodeURIComponent(shipment.shipmentData.dropLocation || '')
    
    // Create a multi-marker Google Maps URL with pickup, current location, and destination
    return `https://www.google.com/maps/dir/${pickup}/${latitude},${longitude}/${drop}`
  }

  const getDirectionsUrl = () => {
    if (!shipment?.shipmentData) return null
    
    const pickup = encodeURIComponent(shipment.shipmentData.pickupLocation || '')
    const drop = encodeURIComponent(shipment.shipmentData.dropLocation || '')
    
    if (locationData?.currentLocation) {
      const { latitude, longitude } = locationData.currentLocation
      return `https://www.google.com/maps/dir/${pickup}/${latitude},${longitude}/${drop}`
    }
    
    return `https://www.google.com/maps/dir/${pickup}/${drop}`
  }

  if (!shipment) return null

  const F = ({ label, value, children }) => (
    <div>
      <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">{label}</p>
      {children || <p className="text-sm text-[#2c3e50]">{value || '—'}</p>}
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
      <div className="bg-[#f8f9fa] rounded-lg w-full max-w-[740px] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[#e9ecef] px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-base font-semibold text-[#2c3e50]">Shipment Location</h2>
          <button onClick={onClose} className="text-[#7f8c8d] hover:text-[#2c3e50] transition-colors text-lg">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Summary */}
          <div className="bg-white border border-[#e9ecef] rounded-lg p-4 grid grid-cols-3 gap-4">
            <F label="Shipment ID" value={shipment.orderId} />
            <F label="Status" value={shipment.status?.toUpperCase()} />
            <F label="Customer" value={shipment.customer || getShipmentRequesterName(shipment.shipmentData || shipment)} />
          </div>

          {/* Route */}
          <div className="bg-white border border-[#e9ecef] rounded-lg p-4">
            <h3 className="text-sm font-semibold text-[#2c3e50] pb-3 mb-3 border-b border-[#e9ecef]">Route Information</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-3 h-3 rounded-full bg-[#27ae60] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-0.5">Pickup</p>
                  <p className="text-sm text-[#2c3e50]">{shipment.shipmentData?.pickupLocation || '—'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-3 h-3 rounded-full bg-[#e74c3c] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-0.5">Drop</p>
                  <p className="text-sm text-[#2c3e50]">{shipment.shipmentData?.dropLocation || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Current Location */}
          <div className="bg-white border border-[#e9ecef] rounded-lg p-4">
            <h3 className="text-sm font-semibold text-[#2c3e50] pb-3 mb-3 border-b border-[#e9ecef]">Current Location</h3>
            {loading ? (
              <div className="flex items-center gap-2 py-4 text-[#7f8c8d] text-sm">
                <div className="w-4 h-4 border-2 border-[#3498db] border-t-transparent rounded-full animate-spin" />
                Loading location data…
              </div>
            ) : error ? (
              <div className="py-4 text-center">
                <p className="text-sm text-[#e74c3c] mb-2">{error}</p>
                <button onClick={fetchLocation} className="text-sm text-[#3498db] hover:underline">Retry</button>
              </div>
            ) : locationData?.currentLocation ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-1">Driver Location</p>
                  <p className="text-sm text-[#2c3e50]">
                    {addressData ? geocodingService.formatAddressFull(addressData) :
                      `${locationData.currentLocation.latitude.toFixed(6)}, ${locationData.currentLocation.longitude.toFixed(6)}`}
                  </p>
                  {addressData && (
                    <p className="text-xs text-[#7f8c8d] mt-0.5">
                      {locationData.currentLocation.latitude.toFixed(6)}, {locationData.currentLocation.longitude.toFixed(6)}
                    </p>
                  )}
                </div>
                {locationData.currentLocation.driver && (
                  <div className="bg-[#f8f9fa] rounded-lg p-3">
                    <p className="text-xs text-[#7f8c8d] font-medium uppercase tracking-wide mb-1">Driver Information</p>
                    <p className="text-sm text-[#2c3e50]">Name: {locationData.currentLocation.driver.name}</p>
                    <p className="text-sm text-[#2c3e50]">Phone: {locationData.currentLocation.driver.phone}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {locationData.currentLocation.speed && <F label="Speed" value={`${locationData.currentLocation.speed} km/h`} />}
                  {locationData.currentLocation.heading && <F label="Heading" value={`${locationData.currentLocation.heading}°`} />}
                  <F label="Last Updated" value={new Date(locationData.currentLocation.timestamp).toLocaleString()} />
                  {locationData.currentLocation.accuracy && <F label="Accuracy" value={`${locationData.currentLocation.accuracy}m`} />}
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#7f8c8d] py-4 text-center">No location data available for this shipment</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {locationData?.currentLocation && (
              <a href={getGoogleMapsUrl()} target="_blank" rel="noopener noreferrer"
                className="flex-1 bg-[#3498db] hover:bg-[#2980b9] text-white text-sm py-2.5 px-4 rounded-lg text-center transition-colors">
                View Route with Current Location
              </a>
            )}
            <a href={getDirectionsUrl()} target="_blank" rel="noopener noreferrer"
              className="flex-1 bg-[#27ae60] hover:bg-[#219a52] text-white text-sm py-2.5 px-4 rounded-lg text-center transition-colors">
              View Directions Only
            </a>
          </div>
          <div className="text-center">
            <button onClick={fetchLocation} disabled={loading}
              className="text-sm bg-[#f8f9fa] border border-[#e9ecef] text-[#7f8c8d] hover:text-[#2c3e50] py-2 px-6 rounded-lg transition-colors disabled:opacity-50">
              {loading ? 'Refreshing…' : 'Refresh Location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LocationModal
